import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { datePopperConfig } from "../Shared/datePopperConfig.js";
import * as FieldTypes from "../Shared/FieldTypes/FieldTypes.js";
import Attachments from "../Shared/Attachments.js";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import EntityAssociation from "../TaskManagerApp/EntityAssociation.js";
import { loadTaskBoards } from "./taskBoardsCache.js";
import "../Shared/Style.css";

const TaskPaneContext = createContext(null);

const noEditing = {
    name: false,
    description: false,
    assignedTo: false,
    priority: false,
    boardStep: false,
    startDate: false,
    dueDate: false,
    estimatedTime: false,
    completed: false,
};

const priorities = [
    { id: 1, name: "High" },
    { id: 2, name: "Medium" },
    { id: 3, name: "Low" },
    { id: 4, name: "None" },
];

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const readableDate = (inputDate, showYear = false) => {
    if (!inputDate)
        return "";
    const date = new Date(inputDate);
    if (isNaN(date.getTime()))
        return "";
    const month = monthNames[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${day} ${month}${showYear ? ` ${year}` : ""}`;
};

const formatDate = (date) => {
    if (!date)
        return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const toDate = (value) => {
    if (!value)
        return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

const displayDescription = (text) => {
    return String(text || "").replace(/</g, "&lt;").replace(/\n/g, "<br/>");
};

const statusName = (value) => (value == 1 || value == "1" || value == "Completed" ? "Completed" : "Incomplete");

const CompletionBadge = ({ value, onlyIcon = false }) => {
    const name = statusName(value);
    return (
        <span style={!onlyIcon ? { padding: "7px 8px", borderRadius: "5px", border: "1px solid #c0c0c0", cursor: "pointer" } : undefined}>
            {name === "Completed"
                ? <i className="fas fa-check-circle" style={{ color: "#00d084" }}></i>
                : <i className="far fa-circle"></i>}
            {!onlyIcon && <span style={{ marginLeft: "5px" }}>{name}</span>}
        </span>
    );
};

const hasIncompleteSubtasks = async (taskId) => {
    const response = await fetch(`/umbraco/api/TaskManagerApi/TaskList?search=&sortBy=DueDate&sortType=ASCENDING&page=1&assignedTo=Company&completionStatus=Incomplete&parentTaskId=${taskId}&linkedEntityType=&linkedEntityId=`);
    const data = response.ok ? await response.json() : [];
    return (data || []).length > 0;
};

const ItemDetailsPane = ({ globalContext }) => {
    const AppContext = globalContext;
    const item = AppContext.currentItem;
    const users = AppContext.users || [];
    const [editing, setEditing] = useState(noEditing);
    const [tab, setTab] = useState("entities");
    const [boards, setBoards] = useState([]);
    const [entityTypes, setEntityTypes] = useState([]);
    const [linkedObjectsList, setLinkedObjectsList] = useState([]);
    const [comments, setComments] = useState([]);
    const [subtasks, setSubtasks] = useState([]);
    const [loadingSubtasks, setLoadingSubtasks] = useState(false);
    const [showItemContextMenu, setShowItemContextMenu] = useState(false);
    const [notifyUser, setNotifyUser] = useState(true);
    const [nameError, setNameError] = useState(false);
    const [isProcessingFollow, setIsProcessingFollow] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [duplicateSuccess, setDuplicateSuccess] = useState(false);
    const [duplicateError, setDuplicateError] = useState(false);
    const [isCheckingSubtasks, setIsCheckingSubtasks] = useState(false);
    const [completionStatusMessage, setCompletionStatusMessage] = useState("");
    const [showDeleteWithSubtasksPopup, setShowDeleteWithSubtasksPopup] = useState(false);
    const nameRef = useRef(null);
    const contextMenuButton = useRef(null);
    const contextMenu = useRef(null);

    const steps = [...(boards?.[0]?.Steps || [])].sort((a, b) => a.BoardStepOrder - b.BoardStepOrder);
    const loading = AppContext.isLoadingItemDetails || !item;
    const assigneeChanged = item && AppContext.originalItem && String(item.AssignedTo) !== String(AppContext.originalItem.AssignedTo);

    useEffect(() => {
        loadTaskBoards()
            .then((data) => setBoards(data || []))
            .catch(() => { });
        fetch("/umbraco/api/TaskManagerApi/Menu?entityType=&entityId=")
            .then((response) => response.ok ? response.json() : null)
            .then((data) => setEntityTypes((data?.Nodes || []).filter((entry) => entry.IsSearchable)))
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (!AppContext.isPaneOpen) {
            setShowItemContextMenu(false);
            setShowDeleteWithSubtasksPopup(false);
        }
    }, [AppContext.isPaneOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                contextMenuButton.current &&
                !contextMenuButton.current.contains(event.target) &&
                contextMenu.current &&
                !contextMenu.current.contains(event.target)
            ) {
                setShowItemContextMenu(false);
                AppContext.setIsShowingDeleteConfirmation(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setEditing(noEditing);
        setTab("entities");
        setNameError(false);
        setNotifyUser(true);
        setDuplicateSuccess(false);
        setDuplicateError(false);
        setIsCheckingSubtasks(false);
        setCompletionStatusMessage("");
        setShowDeleteWithSubtasksPopup(false);
        setLinkedObjectsList(item?.Relationship_Task_Entity || []);
        setComments([]);
        setSubtasks([]);
    }, [item?.Id]);

    const loadSubtasks = (taskId = item?.Id) => {
        if (!taskId)
            return;
        setLoadingSubtasks(true);
        fetch(`/umbraco/api/TaskManagerApi/TaskList?search=&sortBy=DueDate&sortType=ASCENDING&page=1&assignedTo=Company&completionStatus=All&parentTaskId=${taskId}&linkedEntityType=&linkedEntityId=`)
            .then((response) => response.ok ? response.json() : [])
            .then((data) => setSubtasks(data || []))
            .catch(() => setSubtasks([]))
            .finally(() => setLoadingSubtasks(false));
    };

    AppContext.reloadSubtasks = () => loadSubtasks(item?.Id);

    useEffect(() => {
        if (!item?.Id)
            return;
        if (tab === "comments") {
            fetch(`/umbraco/api/TaskManagerApi/Comment?taskId=${item.Id}&page=1`)
                .then((response) => response.ok ? response.json() : [])
                .then((data) => setComments(data || []))
                .catch(() => { });
        }
        if (tab === "subtasks")
            loadSubtasks(item.Id);
    }, [tab, item?.Id]);

    const updateField = (field, value) => {
        AppContext.setCurrentItem({ ...AppContext.currentItem, [field]: value });
    };

    const enableEdit = (field) => {
        setEditing({ ...noEditing, [field]: true });
    };

    const disableEdit = () => setEditing(noEditing);

    const setLinks = (list) => {
        const normalized = (list || []).map((entry) => ({
            RelationshipId: entry.RelationshipId || 0,
            TaskId: item?.Id || 0,
            EntityType: entry.EntityType,
            EntityId: entry.EntityId == null ? "" : String(entry.EntityId),
            EntityName: entry.EntityName || entry.Name || "",
            EntityUrl: entry.EntityUrl || entry.Url || "",
            Deleted: entry.Deleted || 0,
        }));
        setLinkedObjectsList(normalized);
        if (AppContext.currentItem)
            AppContext.setCurrentItem({ ...AppContext.currentItem, Relationship_Task_Entity: normalized });
    };

    const save = async () => {
        if (!item?.Name) {
            setNameError(true);
            return;
        }
        setNameError(false);
        if (item.CompletionStatus == 1 || item.CompletionStatus == "1") {
            if (await hasIncompleteSubtasks(item.Id)) {
                alert("Cannot mark this task as completed because it contains incomplete subtasks.");
                return;
            }
        }
        AppContext.saveCurrentItem();
    };

    const remove = async () => {
        AppContext.deleteCurrentItem({});
    };

    const subtaskCount = Number(item?.SubtaskCount) || 0;

    const requestDelete = () => {
        if (subtaskCount > 0) {
            setShowItemContextMenu(false);
            AppContext.setIsShowingDeleteConfirmation(false);
            setShowDeleteWithSubtasksPopup(true);
            return;
        }
        AppContext.setIsShowingDeleteConfirmation(true);
    };

    const cancelDeleteWithSubtasks = () => {
        setShowDeleteWithSubtasksPopup(false);
        AppContext.setIsShowingDeleteConfirmation(false);
    };

    const follow = (status) => {
        setIsProcessingFollow(true);
        fetch(`/umbraco/api/TaskManagerApi/Follow?follow=${status ? "true" : "false"}&taskId=${item.Id}`, { method: "POST" })
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                if (!data)
                    return;
                const userId = Number(window.currentUserId);
                let followers = (item.Followers || []).filter((id) => id != userId);
                if (status)
                    followers = [...followers, userId];
                AppContext.setCurrentItem({ ...item, Followers: followers });
            })
            .catch(() => { })
            .finally(() => setIsProcessingFollow(false));
    };

    const duplicate = () => {
        setIsDuplicating(true);
        setDuplicateSuccess(false);
        setDuplicateError(false);
        fetch(`/umbraco/api/TaskManagerApi/DuplicateTask?taskId=${item.Id}`, { method: "POST" })
            .then((response) => {
                if (!response.ok)
                    throw new Error();
                return response.json();
            })
            .then((data) => {
                if (data) {
                    setDuplicateSuccess(true);
                    AppContext.refreshData?.();
                }
            })
            .catch(() => setDuplicateError(true))
            .finally(() => setIsDuplicating(false));
    };

    const changeCompletion = async (value) => {
        setCompletionStatusMessage("");
        if (value == "1" && item.SubtaskCount > 0) {
            setIsCheckingSubtasks(true);
            try {
                if (await hasIncompleteSubtasks(item.Id)) {
                    setCompletionStatusMessage("Cannot mark this task as completed because it contains incomplete subtasks.");
                    return;
                }
            } finally {
                setIsCheckingSubtasks(false);
            }
        }
        updateField("CompletionStatus", Number(value));
        disableEdit();
    };

    const paneContext = {
        linkedObjectsList,
        setLinkedObjectsList: setLinks,
        addTaskEntityTypesList: entityTypes,
        useDummy: false,
        clearEntitySearchResults: 0,
        getStaticEntityTypeName: (type) => {
            if (type === "systematic-review-item") return "Systematic Review Items";
            return type;
        },
    };

    const tabStyle = (id) => ({
        cursor: "pointer",
        paddingRight: "10px",
        paddingLeft: id === "entities" ? 0 : "5px",
        paddingBottom: "3px",
        borderBottom: tab === id ? "2px solid #00abff" : "2px solid #f0f0f0",
    });

    return (
        <TaskPaneContext.Provider value={paneContext}>
            <div
                style={{ height: "100vh", overflow: "auto" }}
                className={`slide-pane ${AppContext.isPaneOpen ? "open" : ""}`}
            >
                <div className="content">
                    <div id="fields-content" style={{ flex: 1, flexDirection: "column", paddingLeft: "10px", height: "92vh", overflowY: "auto", position: "relative", display: "flex" }}>
                        <div id="multiple-contents" style={{ flex: 1, overflow: "auto", paddingRight: "15px" }}>
                            <div style={{ flex: "1" }}>
                                {loading ? <LoadingSpinnerImage /> : (
                                    <>
                                        <div style={{ display: "flex", marginBottom: "10px" }}>
                                            <div style={{ flex: 1, marginRight: "20px" }}>
                                                <h3>{item.Name}</h3>
                                            </div>
                                            <div style={{ marginRight: "5px" }}>
                                                <button ref={contextMenuButton} className="close-button" onClick={() => setShowItemContextMenu(!showItemContextMenu)}>
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </button>
                                            </div>
                                            <div>
                                                <button className="close-button" onClick={AppContext.closePane}>
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="view-task-field">
                                            <div className="view-task-field-line">
                                                <div className="view-task-field-title">Name</div>
                                                <div className="view-task-field-value">
                                                    {editing.name === false ? (
                                                        <span
                                                            className={`editable-field-span ${nameError ? "form-field-error" : ""}`}
                                                            onClick={() => {
                                                                enableEdit("name");
                                                                setTimeout(() => {
                                                                    if (nameRef.current) {
                                                                        nameRef.current.selectionStart = 0;
                                                                        nameRef.current.selectionEnd = 0;
                                                                    }
                                                                }, 10);
                                                            }}
                                                        >
                                                            {item.Name}
                                                            {nameError && <span className="form-field-error-message"> The field Name is necessary</span>}
                                                        </span>
                                                    ) : (
                                                        <span style={{ width: "100%" }}>
                                                            <input
                                                                ref={nameRef}
                                                                style={{ width: "100%" }}
                                                                autoFocus
                                                                value={item.Name || ""}
                                                                onBlur={() => { disableEdit(); setNameError(!item.Name); }}
                                                                onChange={(e) => updateField("Name", e.target.value)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="view-task-field">
                                            <div className="view-task-field-line">
                                                <div className="view-task-field-title">Description</div>
                                                <div className="view-task-field-value">
                                                    {editing.description === false ? (
                                                        <div
                                                            className="editable-field-span thin-scrollbar"
                                                            style={{ width: "100%", maxHeight: "300px", overflowY: "auto" }}
                                                            onClick={() => enableEdit("description")}
                                                        >
                                                            {item.Description
                                                                ? <div dangerouslySetInnerHTML={{ __html: displayDescription(item.Description) }} />
                                                                : <span style={{ color: "#c0c0c0" }}>Add a description</span>}
                                                        </div>
                                                    ) : (
                                                        <span style={{ width: "100%" }}>
                                                            <textarea
                                                                style={{ width: "100%", height: "150px" }}
                                                                autoFocus
                                                                value={item.Description || ""}
                                                                onBlur={disableEdit}
                                                                onChange={(e) => updateField("Description", e.target.value)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="view-task-field">
                                            <div className="view-task-field-line">
                                                <div className="view-task-field-title">Assigned to</div>
                                                <div className="view-task-field-value">
                                                    {editing.assignedTo === false ? (
                                                        <div className="editable-field-span" onClick={() => enableEdit("assignedTo")}>
                                                            {item.AssignedTo && users.find((user) => user.Id == item.AssignedTo)
                                                                ? <FieldTypes.FieldType_User value={item.AssignedTo} users={users} />
                                                                : <span className="placeholder-text">Add a user</span>}
                                                        </div>
                                                    ) : (
                                                        <select
                                                            value={item.AssignedTo || ""}
                                                            onBlur={disableEdit}
                                                            onChange={(e) => AppContext.setCurrentItem({ ...item, AssignedTo: Number(e.target.value), NotifyUser: notifyUser })}
                                                        >
                                                            {users.map((user) => <option key={user.Id} value={user.Id}>{user.Name}</option>)}
                                                        </select>
                                                    )}
                                                    <div style={{ width: "100%", display: assigneeChanged ? "flex" : "none", flexDirection: "row", marginTop: "8px", marginLeft: "10px" }}>
                                                        <label>
                                                            <input type="checkbox" checked={notifyUser} onChange={() => { const next = !notifyUser; setNotifyUser(next); updateField("NotifyUser", next); }} /> Notify user by email
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <div style={{ display: "flex", flexDirection: "row" }}>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Priority</div>
                                                            <div className="view-task-field-value">
                                                                {editing.priority === false ? (
                                                                    <span className="editable-field-span editable-field-span-complex" onClick={() => enableEdit("priority")}>
                                                                        <FieldTypes.FieldType_TaskPriority value={item.Priority} />
                                                                    </span>
                                                                ) : (
                                                                    <select value={item.Priority} onBlur={disableEdit} onChange={(e) => updateField("Priority", Number(e.target.value))}>
                                                                        {priorities.map((priority) => <option key={priority.id} value={priority.id}>{priority.name}</option>)}
                                                                    </select>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Created by</div>
                                                            <div className="view-task-field-value">
                                                                <span style={{ display: "flex", flexDirection: "row" }}>
                                                                    <FieldTypes.FieldType_User value={item.CreatedBy} users={users} size="small" />
                                                                    <span style={{ marginLeft: "5px" }}>{readableDate(item.CreatedDate, true)}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "row" }}>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Board</div>
                                                            <div className="view-task-field-value">
                                                                {editing.boardStep === false ? (
                                                                    <span className="editable-field-span" onClick={() => enableEdit("boardStep")}>
                                                                        <FieldTypes.FieldType_TaskBoardStep value={item.BoardStep} />
                                                                    </span>
                                                                ) : (
                                                                    <select value={item.BoardStep || "null"} onBlur={disableEdit} onChange={(e) => updateField("BoardStep", e.target.value === "null" ? null : Number(e.target.value))}>
                                                                        <option value="null">None</option>
                                                                        {steps.map((step) => <option key={step.BoardStepId} value={step.BoardStepId}>{step.BoardStepName}</option>)}
                                                                    </select>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Start date</div>
                                                            <div className="view-task-field-value">
                                                                {editing.startDate === false ? (
                                                                    <span className="editable-field-span" onClick={() => enableEdit("startDate")}>{readableDate(item.StartDate, true)}</span>
                                                                ) : (
                                                                    <DatePicker
                                                                        dateFormat="yyyy-MM-dd"
                                                                        selected={toDate(item.StartDate)}
                                                                        onBlur={disableEdit}
                                                                        onChange={(date) => { updateField("StartDate", formatDate(date)); disableEdit(); }}
                                                                        popperPlacement={datePopperConfig.placement}
                                                                        popperModifiers={datePopperConfig.modifiers}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "row" }}>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Due date</div>
                                                            <div className="view-task-field-value">
                                                                {editing.dueDate === false ? (
                                                                    <span className="editable-field-span" onClick={() => enableEdit("dueDate")}>
                                                                        <FieldTypes.FieldType_TaskDueDate value={item.DueDate} />
                                                                    </span>
                                                                ) : (
                                                                    <DatePicker
                                                                        dateFormat="yyyy-MM-dd"
                                                                        selected={toDate(item.DueDate)}
                                                                        onBlur={disableEdit}
                                                                        onChange={(date) => { updateField("DueDate", formatDate(date)); disableEdit(); }}
                                                                        popperPlacement={datePopperConfig.placement}
                                                                        popperModifiers={datePopperConfig.modifiers}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Estimated time (h)</div>
                                                            <div className="view-task-field-value">
                                                                {editing.estimatedTime === false ? (
                                                                    <span className="editable-field-span" onClick={() => enableEdit("estimatedTime")}>{item.EstimatedTime}h</span>
                                                                ) : (
                                                                    <span>
                                                                        <input
                                                                            style={{ width: "50px" }}
                                                                            autoFocus
                                                                            value={item.EstimatedTime || 0}
                                                                            onBlur={disableEdit}
                                                                            onChange={(e) => updateField("EstimatedTime", e.target.value)}
                                                                        /> h
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "row" }}>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Completed Status</div>
                                                            <div className="view-task-field-value">
                                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                                                    {isCheckingSubtasks ? (
                                                                        <LoadingSpinnerImage small={true} />
                                                                    ) : editing.completed === false ? (
                                                                        <span
                                                                            className="editable-field-span"
                                                                            style={item.CompletionStatus == 0 && item.SubtaskCount > 0 ? { cursor: "pointer" } : undefined}
                                                                            onClick={async () => {
                                                                                setCompletionStatusMessage("");
                                                                                if (item.CompletionStatus == 0 && item.SubtaskCount > 0) {
                                                                                    setIsCheckingSubtasks(true);
                                                                                    try {
                                                                                        if (await hasIncompleteSubtasks(item.Id)) {
                                                                                            setCompletionStatusMessage("Cannot modify completion status. It contains incomplete subtasks.");
                                                                                            return;
                                                                                        }
                                                                                    } finally {
                                                                                        setIsCheckingSubtasks(false);
                                                                                    }
                                                                                }
                                                                                enableEdit("completed");
                                                                            }}
                                                                        >
                                                                            <CompletionBadge value={item.CompletionStatus} />
                                                                        </span>
                                                                    ) : (
                                                                        <select
                                                                            value={item.CompletionStatus}
                                                                            autoFocus
                                                                            onBlur={() => { if (!isCheckingSubtasks) disableEdit(); }}
                                                                            onChange={(e) => changeCompletion(e.target.value)}
                                                                        >
                                                                            <option value={0}>Incomplete</option>
                                                                            <option value={1}>Completed</option>
                                                                        </select>
                                                                    )}
                                                                    {completionStatusMessage && (
                                                                        <div style={{ color: "#d1153a", fontSize: "12px", marginTop: "6px", lineHeight: "1.3" }}>
                                                                            {completionStatusMessage}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ width: "50%" }}>
                                                    <div className="view-task-field">
                                                        <div className="view-task-field-line">
                                                            <div className="view-task-field-title">Follow completion</div>
                                                            <div className="view-task-field-value">
                                                                {(item.Followers || []).map((userId, index) => (
                                                                    <span key={index} style={{ marginLeft: "5px" }}>
                                                                        <FieldTypes.FieldType_User value={userId} users={users} size="small" />
                                                                    </span>
                                                                ))}
                                                                {isProcessingFollow ? <LoadingSpinnerImage small={true} /> : (
                                                                    (item.Followers || []).some((id) => id == window.currentUserId) ? (
                                                                        <span className="follow-button unfollow" onClick={() => follow(false)}>Disable notification</span>
                                                                    ) : (
                                                                        <span className="follow-button" title="Get notified by email when the task is marked as completed" onClick={() => follow(true)}>
                                                                            <i className="fas fa-bell" style={{ marginRight: "8px" }}></i>Get notified when completed
                                                                        </span>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <FieldTypes.FieldType_CustomUserFields globalContext={AppContext} />

                                        <div className="task-details-see-more-separator">
                                            <div className="side"><hr /></div>
                                        </div>

                                        <div style={{ display: "flex", width: "100%", flexDirection: "row", marginBottom: "15px" }}>
                                            <div style={tabStyle("entities")} onClick={() => setTab("entities")}><i className="fas fa-project-diagram g-mr-5"></i> Linked To</div>
                                            <div style={tabStyle("comments")} onClick={() => setTab("comments")}><i className="fas fa-comments g-mr-5"></i> Comments</div>
                                            <div style={tabStyle("subtasks")} onClick={() => setTab("subtasks")}><img style={{ width: "10px", marginRight: "8px" }} src="/assets/images/subtask_icon.png" alt="" />Sub Tasks</div>
                                            <div style={tabStyle("attachments")} onClick={() => setTab("attachments")}><i className="fas fa-paperclip g-mr-5"></i> Attachments</div>
                                            <div style={tabStyle("moreoptions")} onClick={() => setTab("moreoptions")}><i className="fas fa-cog g-mr-5"></i> More options</div>
                                            <div style={{ flex: 1, borderBottom: "2px solid #f0f0f0" }}></div>
                                        </div>

                                        {tab === "entities" && <EntityAssociation context={TaskPaneContext} />}

                                        {tab === "comments" && (
                                            <Comments item={item} users={users} comments={comments} setComments={setComments} />
                                        )}

                                        {tab === "subtasks" && (
                                            loadingSubtasks ? <LoadingSpinnerImage /> : (
                                                <>
                                                    <div className="action-button-bar">
                                                        <div className="action-button-bar-left g-mb-20">
                                                            <button className="action-button save-button" onClick={() => { AppContext.setAddTaskParentTaskId?.(item.Id); AppContext.showAddItemPopup(); }}>Add a subtask</button>
                                                        </div>
                                                    </div>
                                                    <div className="thin-scrollbar" style={{ overflow: "auto", width: "100%" }}>
                                                        <table className="taskManagerTable results-table" style={{ width: "100%" }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: "56%" }}><span className="table-column-filter">SUBTASK NAME</span></th>
                                                                    <th style={{ width: "120px" }}><span className="table-column-filter">STATUS</span></th>
                                                                    <th style={{ width: "14%" }}><span className="table-column-filter">ASSIGNED TO</span></th>
                                                                    <th style={{ width: "14%" }}><span className="table-column-filter">DUE DATE</span></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {subtasks.map((subtask) => (
                                                                    <tr className="results-table-row" key={subtask.Id}>
                                                                        <td style={{ width: "56%", cursor: "pointer" }} onClick={() => AppContext.loadItemDetails(subtask.Id)}>
                                                                            <div title={subtask.Name} className="ellipsis">{subtask.Name}</div>
                                                                        </td>
                                                                        <td style={{ width: "120px" }}><FieldTypes.FieldType_TaskCompletion value={subtask.CompletionStatus} /></td>
                                                                        <td style={{ width: "14%" }}><FieldTypes.FieldType_User value={subtask.AssignedTo} users={users} /></td>
                                                                        <td style={{ width: "14%" }}><FieldTypes.FieldType_TaskDueDate value={subtask.DueDate} /></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {subtasks.length === 0 && <div className="no-more-results">No subtasks</div>}
                                                </>
                                            )
                                        )}

                                        {tab === "attachments" && (
                                            <div style={{ marginBottom: "20px" }}>
                                                <Attachments EntityType="task-manager" EntityId={item.Id} users={users} userRole={window.userRole || "Admin"} />
                                            </div>
                                        )}

                                        {tab === "moreoptions" && (
                                            <div style={{ marginBottom: "20px" }}>
                                                {isDuplicating ? <LoadingSpinnerImage /> : <button className="action-button save-button" onClick={duplicate}>Duplicate this task</button>}
                                                {duplicateSuccess && <span className="duplicate-message-success">This task was duplicated succesfully!</span>}
                                                {duplicateError && <span className="duplicate-message-error">An unexpected problem ocurred while duplicating this task</span>}
                                                <div style={{ color: "#888", marginTop: "25px", width: "100%", fontSize: "9pt" }}>* Duplicating this task will create a new one with the same main fields, Linked To and Sub Tasks</div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div id="buttom-bar" style={{ borderTop: "1px solid #f0f0f0" }}>
                            {!loading && (
                                <div className="action-button-bar">
                                    <div className="action-button-bar-left">
                                        <button className="action-button cancel-button" onClick={AppContext.closePane}>Cancel</button>
                                    </div>
                                    <div className="action-button-bar-center">
                                        <span
                                            className={AppContext.isShowingSaveMessage ? "save-message-shown" : "save-message-hidden"}
                                            onTransitionEnd={() => AppContext.setIsShowingSaveMessage(false)}
                                        >
                                            Save successful!
                                        </span>
                                    </div>
                                    <div className="action-button-bar-right">
                                        {AppContext.isSavingItem ? <LoadingSpinnerImage /> : (
                                            <button className="action-button save-button" onClick={save}>Save</button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showItemContextMenu && (
                <div
                    ref={contextMenu}
                    style={{
                        position: "fixed",
                        top: "50px",
                        right: "70px",
                        background: "white",
                        border: "1px solid #ccc",
                        padding: "4px",
                        borderRadius: "15px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1001,
                    }}
                >
                    {!AppContext.isShowingDeleteConfirmation ? (
                        <div className="context-menu-option" onClick={requestDelete}><i className="far fa-trash-alt"></i> Delete this task</div>
                    ) : (
                        AppContext.isDeletingItem ? <LoadingSpinnerImage /> : (
                            <div className="context-menu-option context-delete-option" onClick={remove}><i className="far fa-trash-alt"></i> Confirm delete?</div>
                        )
                    )}
                </div>
            )}

            {showDeleteWithSubtasksPopup && (
                <div
                    style={{
                        position: "fixed",
                        backgroundColor: "rgba(1, 1, 1, 0.5)",
                        height: "100%",
                        width: "100%",
                        top: 0,
                        left: 0,
                        zIndex: 1100,
                    }}
                >
                    <div
                        className="add-task-popup"
                        style={{ minWidth: "380px", maxWidth: "460px", width: "90%" }}
                    >
                        <div style={{ display: "flex", flexDirection: "row", marginBottom: "5px" }}>
                            <div style={{ width: "50%" }}>
                                <h3>Delete task</h3>
                            </div>
                            <div style={{ width: "50%", textAlign: "right" }}>
                                <button className="close-button" onClick={cancelDeleteWithSubtasks} disabled={AppContext.isDeletingItem}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <div style={{ textAlign: "center", marginTop: "20px", marginBottom: "25px" }}>
                            All the ({subtaskCount}) subtask{subtaskCount === 1 ? "" : "s"} will also be deleted.
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", gap: "10px" }}>
                            {AppContext.isDeletingItem ? (
                                <LoadingSpinnerImage />
                            ) : (
                                <>
                                    <button className="action-button delete-button" onClick={remove}>
                                        Yes, delete all
                                    </button>
                                    <button className="action-button cancel-button" onClick={cancelDeleteWithSubtasks}>
                                        Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </TaskPaneContext.Provider>
    );
};

const Comments = ({ item, users, comments, setComments }) => {
    const [text, setText] = useState("");
    const [edited, setEdited] = useState(null);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);

    const saveComment = () => {
        if (!text) {
            setError(true);
            return;
        }
        setError(false);
        setSaveError(false);
        setSaving(true);
        const body = { Text: text, TaskId: item.Id };
        let method = "POST";
        if (edited) {
            body.Id = edited.Id;
            method = "PUT";
        }
        fetch("/umbraco/api/TaskManagerApi/Comment/", {
            method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                if (!data?.Id) {
                    setSaveError(true);
                    return;
                }
                setText("");
                setEdited(null);
                return fetch(`/umbraco/api/TaskManagerApi/Comment?taskId=${item.Id}&page=1`);
            })
            .then((response) => response && response.ok ? response.json() : null)
            .then((data) => { if (data) setComments(data); })
            .catch(() => setSaveError(true))
            .finally(() => setSaving(false));
    };

    const deleteComment = (commentId) => {
        fetch(`/umbraco/api/TaskManagerApi/Comment?commentId=${commentId}`, { method: "DELETE" })
            .then((response) => response.ok ? response.json() : null)
            .then((data) => { if (data) setComments(comments.filter((entry) => entry.Id != commentId)); })
            .catch(() => { });
    };

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <div><FieldTypes.FieldType_User value={window.currentUserId} users={users} /></div>
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", marginLeft: "10px" }}>
                        <textarea
                            className={`addCommentTextBox form-field-text ${error ? "form-field-error" : ""}`}
                            placeholder="Add a comment"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                        {error && <span className="form-field-error-message">The field Comment is necessary</span>}
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginTop: "10px" }}>
                            {saving ? <LoadingSpinnerImage /> : <button onClick={saveComment} className="action-button save-button">Save comment</button>}
                            {saveError && <div>An error occurred...</div>}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
            <div className="thin-scrollbar" style={{ maxHeight: "100vh", overflow: "auto", paddingRight: "10px" }}>
                {comments.map((comment) => (
                    <CommentRow key={comment.Id} comment={comment} users={users} onDelete={deleteComment} onEdit={(entry) => { setEdited(entry); setText(entry.Text); }} />
                ))}
                <div className="no-more-results">{comments.length > 0 ? "No more comments" : "No comments"}</div>
            </div>
        </>
    );
};

const CommentRow = ({ comment, users, onDelete, onEdit }) => {
    const [showOptions, setShowOptions] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const user = users.find((entry) => entry.Id == comment.CreatedBy);
    const mine = comment.CreatedBy == window.currentUserId;

    return (
        <>
            <div style={{ display: "flex", flexDirection: "row" }}>
                <div><FieldTypes.FieldType_User value={comment.CreatedBy} users={users} /></div>
                <div style={{ display: "flex", flexDirection: "column", flex: 1, marginLeft: "10px" }}>
                    <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                        <div style={{ flex: 1, fontWeight: "bold" }}>{user?.Name}</div>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <div style={{ color: "#a8a8a8", marginRight: "5px" }}>{comment.CreatedDate ? new Date(comment.CreatedDate).toLocaleString() : ""}</div>
                            {mine ? (
                                showOptions ? (
                                    <>
                                        {!confirmDelete && <button className="action-button options-button" title="Delete comment" onClick={() => setConfirmDelete(true)}><i className="fas fa-trash-alt"></i></button>}
                                        {confirmDelete && <button className="action-button delete-button" title="Confirm deletion" onClick={() => onDelete(comment.Id)}><i className="fas fa-trash-alt"></i> Yes, delete</button>}
                                        <button className="action-button options-button" title="Edit comment" onClick={() => onEdit(comment)}><i className="fas fa-pen"></i></button>
                                        <button className="action-button options-button" title="Cancel" onClick={() => { setShowOptions(false); setConfirmDelete(false); }}>Cancel</button>
                                    </>
                                ) : (
                                    <button className="action-button options-button" title="Show options" onClick={() => setShowOptions(true)}><i className="fas fa-ellipsis-v"></i></button>
                                )
                            ) : <div style={{ width: "40px" }}></div>}
                        </div>
                    </div>
                    <div>{comment.Text}</div>
                </div>
            </div>
            <div style={{ marginTop: "20px", borderTop: "1px solid #f3f3f3", marginBottom: "20px" }}></div>
        </>
    );
};

export default ItemDetailsPane;
